import { useAppSelector } from '@/store/hooks';
import { useMemo } from 'react';
import i18n from '@i18n';
import useUser from '@auth/useUser';
import useI18n from '@i18n/useI18n';
import FuseUtils from '@fuse/utils';
import FuseNavigationHelper from '@fuse/utils/FuseNavigationHelper';
import { FuseNavItemType } from '@fuse/core/FuseNavigation/types/FuseNavItemType';
import { selectNavigationAll } from '../store/navigationSlice';
import { AUTH_PLAN_UPDATED_EVENT, getPlan } from '@/utils/localStorage';
import { useEffect, useState } from 'react';

function useNavigation() {
	const { data: user } = useUser();
	const userRole = Array.isArray(user?.role) ? user.role[0] : user?.role;
	const { languageId } = useI18n();

	const navigationData = useAppSelector(selectNavigationAll);
	const [planVersion, setPlanVersion] = useState(0);

	// Add debugging for user role
	if (import.meta.env.NODE_ENV === 'development') {
		console.log('useNavigation user data:', { 
			user: !!user, 
			userRole, 
			originalRole: user?.role,
			navigationDataLength: navigationData.length 
		});
	}

	// Check if Applicant has a plan
	const hasPlan = useMemo(() => {
		if (userRole !== 'Applicant') {
			return true; // For non-applicants, plan doesn't matter
		}

		const plan = getPlan(); // getPlan() already returns parsed object or null

		// Check if plan is empty: null, empty array, or empty object {}
		const isNoPlan =
			!plan ||
			(Array.isArray(plan) && plan.length === 0) ||
			(typeof plan === 'object' && plan !== null && Object.keys(plan).length === 0);

		return !isNoPlan;
	}, [userRole, planVersion]);

	useEffect(() => {
		const handlePlanUpdated = () => {
			setPlanVersion((prev) => prev + 1);
		};

		window.addEventListener(AUTH_PLAN_UPDATED_EVENT, handlePlanUpdated);
		return () => window.removeEventListener(AUTH_PLAN_UPDATED_EVENT, handlePlanUpdated);
	}, []);

	const navigation = useMemo(() => {
		// Add debugging
		if (import.meta.env.NODE_ENV === 'development') {
			console.log('useNavigation processing:', { 
				userRole, 
				navigationDataLength: navigationData.length,
				navigationData: navigationData.slice(0, 3) // First 3 items for debugging
			});
		}

		// Don't process navigation until we have a user role and navigation data from Redux
		// NavigationLoader will filter and set the navigation in Redux based on role and plan
		// We just need to add permissions, disabled state, and translations here
		if (!userRole || navigationData.length === 0) {
			if (import.meta.env.NODE_ENV === 'development') {
				console.log('Returning empty navigation:', { userRole, navigationDataLength: navigationData.length });
			}
			return [];
		}

		const _navigation = FuseNavigationHelper.unflattenNavigation(navigationData);

		if (import.meta.env.NODE_ENV === 'development') {
			console.log('Unflattened navigation:', _navigation.length, _navigation.map(item => ({ id: item.id, title: item.title })));
		}

		function setAdditionalData(data: FuseNavItemType[]): FuseNavItemType[] {
			return data?.map((item) => {
				// Process children first to get their permissions
				let processedChildren: FuseNavItemType[] | undefined;

				if (item.children) {
					processedChildren = setAdditionalData(item.children);
				}

				// Check role-based permission using the auth property
				let itemHasPermission = Boolean(FuseUtils.hasPermission(item?.auth, userRole));
				let itemDisabled = item.disabled || false;

				// For Applicants without a plan, disable and hide all items except /apps/billing/plans
				if (userRole === 'Applicant' && !hasPlan) {
					if (item.id === 'apps.billing.plans' || item.url === '/apps/billing/plans') {
						// Always allow the plans route for Applicants without a plan
						itemHasPermission = true;
						itemDisabled = false;
					} else if (item.id === 'apps.billing') {
						// For billing collapse, check if it has the plans child
						const hasPlansChild = processedChildren?.some((child) => child.id === 'apps.billing.plans');
						// Only show billing collapse if it has the plans child visible
						itemHasPermission = hasPlansChild || false;
						itemDisabled = !hasPlansChild;
					} else {
						// Disable and hide all other items for Applicants without a plan
						itemHasPermission = false;
						itemDisabled = true;
					}
				}

				return {
					...item,
					hasPermission: itemHasPermission,
					disabled: itemDisabled,
					...(item?.translate && item?.title ? { title: i18n.t(`navigation:${item?.translate}`) } : {}),
					...(processedChildren ? { children: processedChildren } : {})
				};
			});
		}

		const translatedValues = setAdditionalData(_navigation);

		// Final filter: Remove items without permission as a safety net
		// (Navigation components also check hasPermission, but this ensures clean data)
		const filteredValues = translatedValues.filter((item) => {
			// Keep items with permission
			if (item.hasPermission) {
				// If it has children, filter them too
				// Only check children if the item actually has children (not just an empty array)
				if (item.children && item.children.length > 0) {
					const filteredChildren = item.children.filter((child) => {
						return child.hasPermission;
					});

					if (filteredChildren.length === 0) {
						// If no children have permission, hide the parent too
						return false;
					}

					item.children = filteredChildren;
				}

				return true;
			}

			return false;
		});

		if (import.meta.env.NODE_ENV === 'development') {
			console.log('Final filtered navigation:', filteredValues.length, filteredValues.map(item => ({ id: item.id, title: item.title, hasPermission: item.hasPermission })));
		}

		return filteredValues;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navigationData, userRole, languageId, hasPlan]);

	const flattenNavigation = useMemo(() => {
		return FuseNavigationHelper.flattenNavigation(navigation);
	}, [navigation]);

	return { navigation, flattenNavigation };
}

export default useNavigation;
